/**
 * L'ossature du planning à plat, et ce que le pliage en laisse voir.
 *
 * Deux fonctions pures, sorties de `Planning.tsx` le 24/08/2026 quand les chapitres sont
 * devenus pliables. Elles y étaient inline ; elles en sortent parce que **la propriété qui
 * compte n'est pas visible à l'œil** : la mise à plat porte le chantier ENTIER — c'est elle
 * qui donne « 12 tâches sur 17 » et l'étendue de la frise —, et seul l'affichage se
 * restreint à ce qui est ouvert. Brancher un compteur sur ce qui est visible ferait tomber
 * l'en-tête à « 0 tâche sur 0 » à l'arrivée sur l'écran, tout étant replié au départ.
 *
 * C'est le même raisonnement que `barreGrise.ts` : ce que l'écran retient se vérifie sur la
 * couche qui le décide, sans quoi un essai vert sur une autre couche donne une fausse
 * assurance.
 */
import type { NoeudVue, OuvrageVue } from "@planif/moteur";

/**
 * Une ligne du tableau : un titre de chapitre, ou une tâche.
 *
 * *Demandé par Chris le 12/08/2026 : « je veux voir en face de TRANCHE FERME du premier au
 * dernier tout ce qu'elle contient, mais je veux aussi voir tout le détail ».*
 *
 * L'écran ne montrait que les tâches, sous un simple titre reprenant leur chapitre PARENT
 * DIRECT — « 1.1 FRANCHISSEMENT ». Les niveaux au-dessus, « 1 TRANCHE FERME »,
 * n'apparaissaient nulle part : sur un déboursé à trois niveaux, la moitié de l'ossature du
 * classeur était invisible, et avec elle toute vue d'ensemble. Chapitres et tâches sortent
 * donc dans le même flux, chacun avec sa profondeur — l'ordre du classeur.
 */
export type LigneGantt = {
  readonly profondeur: number;
  /**
   * Les numéros des chapitres au-dessus de cette ligne, du plus haut au plus proche.
   *
   * C'est ce qui rend le pliage possible sans fabriquer un second arbre : une ligne se
   * montre si **tous** ses ancêtres sont ouverts. Un seul ancêtre fermé la retire, même si
   * son parent direct est ouvert — sinon refermer « 1 » laisserait à l'écran les tâches de
   * « 1.1 », orphelines de leur titre.
   */
  readonly ancetres: readonly string[];
} & (
  | { readonly nature: "chapitre"; readonly chapitre: NoeudVue }
  | { readonly nature: "tache"; readonly tache: OuvrageVue }
);

/** L'arbre du déboursé à plat : **chaque** chapitre, puis **chaque** tâche. */
export function aplatir(
  noeuds: readonly NoeudVue[],
  profondeur = 0,
  ancetres: readonly string[] = [],
): LigneGantt[] {
  const lignes: LigneGantt[] = [];
  for (const noeud of noeuds) {
    // Un poste écarté du chiffrage ne se planifie pas : il ne coûte rien, il
    // n'occupe personne. L'afficher ferait une ligne vide de plus sur cent.
    if (!noeud.retenu) continue;
    if (noeud.nature === "ouvrage") {
      lignes.push({ nature: "tache", tache: noeud, profondeur, ancetres });
    } else {
      lignes.push({ nature: "chapitre", chapitre: noeud, profondeur, ancetres });
      lignes.push(...aplatir(noeud.enfants, profondeur + 1, [...ancetres, noeud.numero]));
    }
  }
  return lignes;
}

/**
 * Ce qui reste à l'écran, compte tenu des chapitres ouverts.
 *
 * *Demande de Chris, 24/08/2026 : « en vue planning tout est replié ; je voudrais une flèche
 * à côté des chapitres pour n'ouvrir que le chapitre, et le refermer ».*
 *
 * Un ensemble **vide laisse les chapitres de premier niveau** : ils n'ont aucun ancêtre, donc
 * rien à attendre. C'est l'état de départ voulu — l'ossature du chantier, et le résumé de
 * chaque chapitre sur la frise, sans une ligne de détail.
 */
export function lignesVisibles(
  lignes: readonly LigneGantt[],
  chapitresOuverts: ReadonlySet<string>,
): LigneGantt[] {
  return lignes.filter((ligne) => ligne.ancetres.every((numero) => chapitresOuverts.has(numero)));
}

/**
 * Les lignes de ressource d'une tâche que le planning montre.
 *
 * ## Une ligne SUPPRIMÉE ne se planifie pas — décision de Chris, 10/09/2026
 *
 * *« Quand une ligne est retirée dans la contre-étude, nous avons validé de laisser la ligne
 * apparaître dans la contre-étude avec la mention supprimé et la ligne en grisé, mais par
 * contre pas la peine de la faire apparaître dans le planning si elle est supprimée. »*
 *
 * **Deux écrans, deux questions — d'où deux traitements.** La contre-étude compare au devis :
 * une ligne écartée doit s'y voir, sinon l'écart avec le classeur devient inexplicable, d'où la
 * mention et le grisé. Le planning dit qui travaille quel jour : une ligne retirée ne coûte
 * rien, n'occupe personne et ne porte aucune barre. L'afficher n'ajoutait qu'une piste vide de
 * plus à faire défiler.
 *
 * Le moteur l'écartait **déjà de tous ses calculs** — heures, durées, déboursé, attelages — mais
 * pas de cette liste : le chiffrage était juste, l'écran seul montrait des lignes mortes. C'est
 * la raison d'être de ce module, écrite dans son en-tête : ce que l'écran retient se vérifie sur
 * la couche qui le décide.
 *
 * ## L'ordre des trois filtres
 *
 * Le retrait passe **avant** les deux autres et non à leur place : une ligne supprimée qui part
 * sur porte-engin, ou qui est acheminée par nos soins, reste une ligne supprimée. Les inverser
 * ferait réapparaître sous un filtre ce que la vue complète cache — et un filtre qui montre
 * *plus* que la vue entière est le genre d'incohérence qu'on ne cherche jamais au bon endroit.
 */
export function lignesDeLaTache<
  R extends { readonly statut?: string },
  T extends { readonly ressources: readonly R[] },
>(
  tache: T,
  options: {
    /** Ce que le filtre « matériel transférable » garde. */
    readonly surPorteEngin: (ressource: R) => boolean;
    /** Ce que le filtre « livraisons par nos soins » garde, déjà calculé sur la tâche. */
    readonly acheminees: () => readonly R[];
    readonly filtreMateriel: boolean;
    readonly filtreAcheminement: boolean;
    /** Le statut d'une ligne écartée du chiffrage. Passé pour que ce module reste sans import. */
    readonly statutRetire: string;
  },
): readonly R[] {
  const vivante = (r: R): boolean => r.statut !== options.statutRetire;
  if (options.filtreMateriel) return tache.ressources.filter((r) => vivante(r) && options.surPorteEngin(r));
  if (options.filtreAcheminement) return options.acheminees().filter(vivante);
  return tache.ressources.filter(vivante);
}
